import { CheckIcon, ClipboardIcon } from '@heroicons/react/24/outline'
import React, { useState } from 'react'

/**
 * Renders the markdown the assistant actually writes: headings, ordered and unordered lists
 * (nested), fenced code, tables, blockquotes, links, and inline emphasis.
 *
 * This is deliberately a parser that builds React elements rather than HTML. The input is model
 * output, and some of it quotes borrower names, notes, and documents, so it must never reach
 * dangerouslySetInnerHTML - building elements makes injected markup impossible rather than
 * something we have to sanitise correctly every time.
 *
 * AiTextRender remains for the older prompt surfaces; it only understands `*` bullets and bold,
 * which is why anything richer used to arrive as raw characters in the chat.
 */

interface MarkdownRenderProps {
  text: string
  className?: string
}

/** Only schemes that are safe to hand to the browser from model output. */
const SAFE_LINK = /^(https?:\/\/|mailto:)/i

const INLINE_PATTERN = new RegExp(
  [
    '`([^`]+)`', // code
    '\\*\\*([^*]+)\\*\\*', // bold
    '__([^_]+)__', // bold
    '~~([^~]+)~~', // strikethrough
    '\\*([^*\\n]+)\\*', // italic
    '\\[([^\\]]+)\\]\\(([^)\\s]+)\\)', // link
  ].join('|'),
  'g',
)

/** Emphasis, code spans and links within a single line. */
function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null
  let i = 0

  INLINE_PATTERN.lastIndex = 0
  while ((match = INLINE_PATTERN.exec(text)) !== null) {
    if (match.index > lastIndex) nodes.push(text.slice(lastIndex, match.index))
    const [full, code, boldStar, boldUnderscore, strike, italic, linkText, linkHref] = match
    const key = `${keyPrefix}-i${i++}`

    if (code !== undefined) {
      nodes.push(
        <code key={key} className="rounded bg-gray-100 px-1 py-0.5 font-mono text-[0.85em] text-gray-800">
          {code}
        </code>,
      )
    } else if (boldStar !== undefined || boldUnderscore !== undefined) {
      nodes.push(<strong key={key}>{boldStar ?? boldUnderscore}</strong>)
    } else if (strike !== undefined) {
      nodes.push(
        <span key={key} className="line-through">
          {strike}
        </span>,
      )
    } else if (italic !== undefined) {
      nodes.push(<em key={key}>{italic}</em>)
    } else if (linkText !== undefined && SAFE_LINK.test(linkHref)) {
      nodes.push(
        <a
          key={key}
          href={linkHref}
          target="_blank"
          rel="noreferrer noopener"
          className="text-shade-blue underline hover:no-underline"
        >
          {linkText}
        </a>,
      )
    } else {
      // An unsupported or unsafe link scheme: show the text, never the href.
      nodes.push(<span key={key}>{linkText ?? full}</span>)
    }
    lastIndex = match.index + full.length
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex))
  return nodes.length ? nodes : [text]
}

function CodeBlock({ code, language }: { code: string; language?: string }) {
  const [copied, setCopied] = useState(false)

  const onCopy = () => {
    navigator.clipboard?.writeText(code).then(
      () => {
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
      },
      () => {},
    )
  }

  return (
    <div className="my-3 overflow-hidden rounded-lg border border-gray-800 bg-gray-900">
      <div className="flex items-center justify-between border-b border-gray-800 px-3 py-1.5">
        <span className="font-mono text-xs text-gray-400">{language || 'code'}</span>
        <button
          type="button"
          onClick={onCopy}
          className="flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-gray-400 hover:bg-gray-800 hover:text-gray-200"
          aria-label="Copy code"
        >
          {copied ? <CheckIcon className="h-3.5 w-3.5" /> : <ClipboardIcon className="h-3.5 w-3.5" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="overflow-x-auto px-3 py-2.5">
        <code className="font-mono text-[0.8rem] leading-relaxed text-gray-100">{code}</code>
      </pre>
    </div>
  )
}

/** A pipe table, split on unescaped pipes with the outer delimiters trimmed. */
function splitRow(line: string): string[] {
  return line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => cell.trim())
}

const isTableDivider = (line: string) => /^\s*\|?[\s:-]*-[\s|:-]*\|?\s*$/.test(line) && line.includes('-')

interface ListItem {
  content: string
  indent: number
  ordered: boolean
}

interface ListNode {
  content: string
  ordered: boolean
  children: ListNode[]
}

/** Group consecutive list lines into a tree, using leading indentation for depth. */
function buildListTree(items: ListItem[], start: number, indent: number): [ListNode[], number] {
  const nodes: ListNode[] = []
  let i = start

  while (i < items.length && items[i].indent >= indent) {
    if (items[i].indent > indent) {
      const [children, next] = buildListTree(items, i, items[i].indent)
      // A deeper run belongs to the item directly above it.
      if (nodes.length) nodes[nodes.length - 1].children = children
      i = next
      continue
    }
    nodes.push({ content: items[i].content, ordered: items[i].ordered, children: [] })
    i += 1
  }
  return [nodes, i]
}

function renderListNodes(nodes: ListNode[], keyPrefix: string): React.ReactNode {
  if (!nodes.length) return null
  const ordered = nodes[0].ordered
  const ListTag = ordered ? 'ol' : 'ul'
  return (
    <ListTag className={`${ordered ? 'list-decimal' : 'list-disc'} my-2 space-y-1 pl-5 marker:text-gray-400`}>
      {nodes.map((node, index) => (
        <li key={`${keyPrefix}-${index}`} className="leading-relaxed">
          {renderInline(node.content, `${keyPrefix}-${index}`)}
          {renderListNodes(node.children, `${keyPrefix}-${index}-c`)}
        </li>
      ))}
    </ListTag>
  )
}

/** Nested lists, rebuilt from the indentation of consecutive list lines. */
function renderList(items: ListItem[], keyPrefix: string): React.ReactNode {
  const [tree] = buildListTree(items, 0, items[0].indent)
  return renderListNodes(tree, keyPrefix)
}

export function MarkdownRender({ text, className = '' }: MarkdownRenderProps) {
  if (!text || typeof text !== 'string') return <div className={className} />

  const lines = text.replace(/\r\n/g, '\n').split('\n')
  const blocks: React.ReactNode[] = []
  let key = 0
  let i = 0

  while (i < lines.length) {
    const line = lines[i]

    // Fenced code
    const fence = line.match(/^\s*```(\w+)?\s*$/)
    if (fence) {
      const language = fence[1]
      const body: string[] = []
      i += 1
      while (i < lines.length && !/^\s*```\s*$/.test(lines[i])) {
        body.push(lines[i])
        i += 1
      }
      i += 1 // closing fence
      blocks.push(<CodeBlock key={key++} code={body.join('\n')} language={language} />)
      continue
    }

    // Heading
    const heading = line.match(/^(#{1,6})\s+(.*)$/)
    if (heading) {
      const level = heading[1].length
      const size = level <= 1 ? 'text-lg' : level === 2 ? 'text-base' : 'text-sm'
      blocks.push(
        <p key={key++} className={`${size} mt-4 mb-1.5 font-semibold text-gray-900 first:mt-0`}>
          {renderInline(heading[2], `h${key}`)}
        </p>,
      )
      i += 1
      continue
    }

    // Horizontal rule
    if (/^\s*([-*_])\s*\1\s*\1[\s\-*_]*$/.test(line)) {
      blocks.push(<hr key={key++} className="my-3 border-gray-200" />)
      i += 1
      continue
    }

    // Table
    if (line.includes('|') && i + 1 < lines.length && isTableDivider(lines[i + 1])) {
      const header = splitRow(line)
      i += 2
      const rows: string[][] = []
      while (i < lines.length && lines[i].includes('|') && lines[i].trim()) {
        rows.push(splitRow(lines[i]))
        i += 1
      }
      blocks.push(
        <div key={key++} className="my-3 overflow-x-auto">
          <table className="min-w-full border-collapse text-left text-[0.85rem]">
            <thead>
              <tr className="border-b border-gray-300">
                {header.map((cell, index) => (
                  <th key={index} className="px-2.5 py-1.5 font-semibold text-gray-700">
                    {renderInline(cell, `th${key}-${index}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex} className="border-b border-gray-100 last:border-0">
                  {row.map((cell, cellIndex) => (
                    <td key={cellIndex} className="px-2.5 py-1.5 align-top text-gray-700">
                      {renderInline(cell, `td${key}-${rowIndex}-${cellIndex}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      )
      continue
    }

    // Blockquote
    if (/^\s*>\s?/.test(line)) {
      const quoted: string[] = []
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) {
        quoted.push(lines[i].replace(/^\s*>\s?/, ''))
        i += 1
      }
      blocks.push(
        <blockquote key={key++} className="my-2 border-l-2 border-gray-300 pl-3 text-gray-600">
          {renderInline(quoted.join(' '), `q${key}`)}
        </blockquote>,
      )
      continue
    }

    // Lists
    const listMatch = line.match(/^(\s*)([-*+]|\d+[.)])\s+(.*)$/)
    if (listMatch) {
      const items: ListItem[] = []
      while (i < lines.length) {
        const match = lines[i].match(/^(\s*)([-*+]|\d+[.)])\s+(.*)$/)
        if (!match) break
        items.push({
          indent: match[1].length,
          ordered: /\d/.test(match[2]),
          content: match[3],
        })
        i += 1
      }
      blocks.push(<React.Fragment key={key++}>{renderList(items, `ul${key}`)}</React.Fragment>)
      continue
    }

    // Blank line
    if (!line.trim()) {
      i += 1
      continue
    }

    // Paragraph: gather until a blank line or the start of another block.
    const paragraph: string[] = []
    while (i < lines.length && lines[i].trim() && !/^(\s*```|#{1,6}\s|\s*>|\s*([-*+]|\d+[.)])\s)/.test(lines[i])) {
      paragraph.push(lines[i].trim())
      i += 1
    }
    if (paragraph.length) {
      blocks.push(
        <p key={key++} className="my-1.5 leading-relaxed first:mt-0 last:mb-0">
          {renderInline(paragraph.join(' '), `p${key}`)}
        </p>,
      )
    }
  }

  return <div className={className}>{blocks}</div>
}
